import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ShieldAlert, LayoutDashboard, Building2, ScrollText, LogOut, KeyRound, Inbox, AlertOctagon, Megaphone } from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

const navItems = [
  { to: "/master", label: "Panoramica", icon: LayoutDashboard, end: true },
  { to: "/master/agenzie", label: "Agenzie", icon: Building2 },
  { to: "/master/pagamenti", label: "Pagamenti a rischio", icon: AlertOctagon, badgeKey: "at_risk_count" },
  { to: "/master/richieste", label: "Richieste call", icon: Inbox },
  { to: "/master/comunicazioni", label: "Comunicazioni", icon: Megaphone },
  { to: "/master/audit", label: "Audit log", icon: ScrollText },
];

export default function MasterLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [badges, setBadges] = useState({});

  useEffect(() => {
    if (!loading && (!user || user.role !== "platform_admin")) {
      nav("/master/login", { replace: true });
    }
  }, [user, loading, nav]);

  useEffect(() => {
    if (!user || user.role !== "platform_admin") return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/master/dashboard");
        if (!cancelled) setBadges({ at_risk_count: r.data?.at_risk_count || 0 });
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  useEffect(() => {
    if (!loading && (!user || user.role !== "platform_admin")) {
      nav("/master/login", { replace: true });
    }
  }, [user, loading, nav]);

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex">
      <aside className="w-64 border-r border-slate-800 bg-slate-900 flex flex-col">
        <div className="p-5 border-b border-slate-800 flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-amber-500 text-slate-900 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold leading-tight">Master</div>
            <div className="font-heading font-bold leading-tight">Console</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((n) => {
            const badgeCount = n.badgeKey ? (badges[n.badgeKey] || 0) : 0;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
                    isActive ? "bg-amber-500 text-slate-900 font-semibold" : "text-slate-300 hover:bg-slate-800"
                  }`
                }
                data-testid={`master-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <n.icon className="w-4 h-4" />
                <span className="flex-1">{n.label}</span>
                {badgeCount > 0 ? (
                  <span
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-500 text-white"
                    data-testid={`master-nav-badge-${n.badgeKey}`}
                  >
                    {badgeCount}
                  </span>
                ) : null}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="text-xs text-slate-400 mb-2">{user.username}</div>
          <Button
            variant="outline"
            size="sm"
            className="w-full border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 mb-2"
            onClick={() => setChangePwdOpen(true)}
            data-testid="master-change-password"
          >
            <KeyRound className="w-3.5 h-3.5 mr-2" /> Cambia password
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-full border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800"
            onClick={async () => { await logout(); nav("/master/login"); }}
            data-testid="master-logout"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" /> Esci
          </Button>
        </div>
      </aside>
      <main className="flex-1 p-6 overflow-x-auto">
        <Outlet />
      </main>
      <ChangePasswordDialog open={changePwdOpen} onOpenChange={setChangePwdOpen} />
    </div>
  );
}
